import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Calculator, Save, Download } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './TileCoverageCalculatorPage/components/CalculationForm';
import ResultsOverview from './TileCoverageCalculatorPage/components/ResultsOverview';
import MaterialsTable from './TileCoverageCalculatorPage/components/MaterialsTable';
import QuickAdjustmentSidebar from './TileCoverageCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './TileCoverageCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './TileCoverageCalculatorPage/components/TipsSidebar';
import InfoContent from './TileCoverageCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  // Параметры помещения
  areaType: 'room', // 'room', 'bathroom', 'kitchen', 'custom'
  roomShape: 'rectangle', // 'rectangle', 'l-shaped', 'complex'
  roomWidth: '4', // метры
  roomLength: '5', // метры
  roomHeight: '2.7', // метры
  additionalArea: '0', // дополнительная площадь для L-образных и сложных помещений
  wallsToTile: ['all'], // 'all', 'north', 'south', 'east', 'west'
  
  // Параметры плитки
  tileType: 'ceramic', // 'ceramic', 'porcelain', 'natural_stone', 'mosaic'
  tileShape: 'square', // 'square', 'rectangle', 'hexagon', 'custom'
  tileWidth: '30', // сантиметры
  tileLength: '30', // сантиметры
  tileThickness: '0.8', // сантиметры
  tilesInPackage: '10', // штук
  packageArea: '0.9', // кв. метры
  packagePrice: '1200', // рубли
  
  // Параметры укладки
  layoutPattern: 'straight', // 'straight', 'diagonal', 'herringbone', 'brick'
  seamWidth: '2', // миллиметры
  wasteFactor: '10', // процент запаса (брак, подрезка)
  
  // Дополнительные материалы
  includePrimerCost: true,
  includeAdhesiveCost: true,
  includeGroutCost: true,
  
  // Стоимость работ
  includeLaborCost: false,
  laborPricePerMeter: '700', // рубли за кв. метр
};

// Определение типов помещений
export const areaTypes = [
  { id: 'room', name: 'Жилая комната', defaultHeight: 2.7 },
  { id: 'bathroom', name: 'Ванная комната', defaultHeight: 2.5 },
  { id: 'kitchen', name: 'Кухня', defaultHeight: 2.7 },
  { id: 'custom', name: 'Произвольное помещение', defaultHeight: 2.5 }
];

// Определение типов плитки
export const tileTypes = [
  { id: 'ceramic', name: 'Керамическая плитка' },
  { id: 'porcelain', name: 'Керамогранит' },
  { id: 'natural_stone', name: 'Натуральный камень' },
  { id: 'mosaic', name: 'Мозаика' }
];

// Определение типов укладки
export const layoutPatterns = [
  { id: 'straight', name: 'Прямая укладка', wasteFactor: 10 },
  { id: 'diagonal', name: 'Диагональная укладка', wasteFactor: 15 },
  { id: 'herringbone', name: 'Ёлочка', wasteFactor: 15 },
  { id: 'brick', name: 'Кирпичная кладка', wasteFactor: 12 }
];

/**
 * TileCoverageCalculatorPage Component - Полный калькулятор расхода плитки с учетом различных параметров помещения и материалов.
 */
const TileCoverageCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('tile-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор расхода плитки | Gensio';
  const metaDescription = tool?.metaDescription || 'Расчет необходимого количества плитки для стен и пола, расчет стоимости и количества материалов для укладки: плитки, клея, затирки с учетом типа помещения и раскладки.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [materialsData, setMaterialsData] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedTileCalculations');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'bathroom':
          newState.areaType = 'bathroom';
          newState.roomWidth = '2.5';
          newState.roomLength = '2';
          newState.roomHeight = '2.5';
          break;
        case 'kitchen':
          newState.areaType = 'kitchen';
          newState.wallsToTile = ['north', 'east'];
          break;
        case 'floor':
          newState.wallsToTile = [];
          break;
        case 'walls':
          newState.wallsToTile = ['all'];
          break;
        case 'mosaic':
          newState.tileType = 'mosaic';
          newState.tileWidth = '2.5';
          newState.tileLength = '2.5';
          newState.tilesInPackage = '10';
          newState.packageArea = '0.6';
          break;
        case 'porcelain':
          newState.tileType = 'porcelain';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setMaterialsData([]);
    setErrors({});
    setShowDetails(false);

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalc = {
      id: Date.now().toString(),
      name: name || `Расчет ${formData.areaType === 'bathroom' ? 'ванной' : formData.areaType === 'kitchen' ? 'кухни' : 'комнаты'} ${formData.roomWidth}×${formData.roomLength}м`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCalcs = [...savedCalculations, newSavedCalc];
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedTileCalculations', JSON.stringify(updatedSavedCalcs));

    return newSavedCalc;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalcs = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedTileCalculations', JSON.stringify(updatedSavedCalcs));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
      // Создадим базовые материалы для визуализации
      if (calcToLoad.results) {
        generateMaterialsData(calcToLoad.results);
      }
    }
  };

  // Генерация данных о материалах на основе результатов
  const generateMaterialsData = (calculatedResults) => {
    if (!calculatedResults) return;
    
    const materials = [
      {
        type: 'tiles',
        name: 'Плитка',
        quantity: `${calculatedResults.totalTilesNeeded} шт.`,
        packages: `${calculatedResults.packagesNeeded} уп.`,
        totalArea: `${calculatedResults.totalTileArea.toFixed(2)} м²`,
        price: `${calculatedResults.totalTileCost.toFixed(2)} ₽`,
        notes: calculatedResults.wasteFactor > 0 ? `Включая запас ${calculatedResults.wasteFactor}%` : ''
      }
    ];

    if (formData.includeAdhesiveCost && calculatedResults.adhesiveNeeded) {
      materials.push({
        type: 'adhesive',
        name: 'Плиточный клей',
        quantity: `${calculatedResults.adhesiveNeeded.toFixed(2)} кг`,
        packages: `${calculatedResults.adhesivePackages} мешков`,
        totalArea: `${calculatedResults.totalTileArea.toFixed(2)} м²`,
        price: `${calculatedResults.adhesiveCost.toFixed(2)} ₽`,
        notes: 'Расчет по средним нормам расхода'
      });
    }

    if (formData.includeGroutCost && calculatedResults.groutNeeded) {
      materials.push({
        type: 'grout',
        name: 'Затирка для швов',
        quantity: `${calculatedResults.groutNeeded.toFixed(2)} кг`,
        packages: `${calculatedResults.groutPackages} уп.`,
        totalArea: `${(calculatedResults.seamArea || 0).toFixed(2)} м²`,
        price: `${calculatedResults.groutCost.toFixed(2)} ₽`,
        notes: `Для швов шириной ${formData.seamWidth} мм`
      });
    }

    if (formData.includePrimerCost && calculatedResults.primerNeeded) {
      materials.push({
        type: 'primer',
        name: 'Грунтовка',
        quantity: `${calculatedResults.primerNeeded.toFixed(2)} л`,
        packages: `${calculatedResults.primerPackages} шт.`,
        totalArea: `${calculatedResults.totalArea.toFixed(2)} м²`,
        price: `${calculatedResults.primerCost.toFixed(2)} ₽`,
        notes: 'Для подготовки поверхности'
      });
    }

    if (formData.includeLaborCost && calculatedResults.laborCost) {
      materials.push({
        type: 'labor',
        name: 'Работа по укладке',
        quantity: '-',
        packages: '-',
        totalArea: `${calculatedResults.totalTileArea.toFixed(2)} м²`,
        price: `${calculatedResults.laborCost.toFixed(2)} ₽`,
        notes: `${formData.laborPricePerMeter} ₽/м²`
      });
    }

    setMaterialsData(materials);
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Grid className="w-8 h-8 text-fuchsia-600 dark:text-fuchsia-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор расхода плитки'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              generateMaterialsData={generateMaterialsData}
              areaTypes={areaTypes}
              tileTypes={tileTypes}
              layoutPatterns={layoutPatterns}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <MaterialsTable 
                  materials={materialsData} 
                  showDetails={showDetails}
                  setShowDetails={setShowDetails}
                />
              </>
            )}

            {/* --- Информационный контент о плитке --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default TileCoverageCalculatorPage;